// Cost (screen 4): the seat's ledger and roll-up for the month of the focused week, from
// `GET /seats/{id}/cost?period_type=month` (one call). Detail tabs by role (13 §Principals):
// - agency operator: "Client" and "Agency" ledgers (a `Segmented` switch, design spec §9);
// - owner / admin: the "Client" ledger only (agency rows are never sent to them);
// - client viewer: client-visibility totals, no ledger, no agency tab.
import {
  EmptyState,
  InfoTip,
  Segmented,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { ReceiptTextIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { CostEvent, SeatCost } from "../../api/types";
import { weekStart } from "../../focus/weeks";
import type { CompanyAccess } from "../company";
import { isManager } from "../company";
import { COST_KIND_LABELS, formatDateTime, formatMoney } from "../format";
import { seatBoardWakesQuery, seatCostQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";

/** The month (`2026-10`) a week belongs to: the month of its Thursday (ISO rule). */
export function monthOfWeek(week: string): string {
  const thursday = new Date(weekStart(week).getTime() + 3 * 86_400_000);
  return thursday.toISOString().slice(0, 7);
}

function monthLabel(month: string): string {
  return new Date(`${month}-15T12:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function CostTab({
  api,
  company,
  seatId,
  week,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  week: string;
}) {
  const month = monthOfWeek(week);
  const cost = useQuery(seatCostQuery(api, company.id, seatId, month));
  const [detail, setDetail] = useState<"client" | "agency">("client");
  if (cost.isPending) return <Loading label="Loading cost" rows={4} />;
  if (cost.isError) return <LoadError what="Cost" error={cost.error} />;
  const operator = company.roles.includes("agency_operator");
  const manager = isManager(company);
  return (
    <div className="flex min-w-0 flex-col gap-4" data-slot="de-cost">
      <Rollup cost={cost.data} month={month} />
      {manager ? <BoardWakes api={api} co={company.id} seatId={seatId} /> : null}
      {manager ? (
        <div className="flex min-w-0 flex-col gap-3">
          {operator ? (
            <Segmented
              aria-label="Cost detail"
              size="sm"
              value={detail}
              onValueChange={setDetail}
              options={[
                { value: "client", label: "Client" },
                { value: "agency", label: "Agency" },
              ]}
            />
          ) : null}
          <Ledger
            rows={cost.data.ledger.filter((e) => e.visibility === detail)}
            which={detail === "agency" ? "Agency" : "Client"}
          />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Totals of the costs shared with you. Line items are kept by the company's managers.
        </p>
      )}
    </div>
  );
}

function Rollup({ cost, month }: { cost: SeatCost; month: string }) {
  const kinds = Object.entries(cost.rollup.by_kind).filter(([, v]) => v !== 0);
  return (
    <Table>
      <TableCaption>Cost by kind, {monthLabel(month)}.</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">Kind</TableHead>
          <TableHead scope="col" className="text-right">
            Amount
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {kinds.length ? (
          kinds.map(([kind, amount]) => (
            <TableRow key={kind} data-kind={kind}>
              <TableHead scope="row" className="font-normal">
                {COST_KIND_LABELS[kind] ?? kind}
              </TableHead>
              <TableCell className="text-right tabular-nums">
                {formatMoney(amount, cost.rollup.currency)}
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={2} className="text-muted-foreground">
              No cost this month.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableHead scope="row">Total</TableHead>
          <TableCell className="text-right tabular-nums" data-field="total">
            {formatMoney(cost.rollup.total_minor, cost.rollup.currency)}
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );
}

/**
 * T2-BOARD-WAKE: the boards this DE owns and how often they woke it over the last 7 days (wakes a
 * day, events a wake, today against the board's cap). Nothing when it owns no board.
 */
function BoardWakes({ api, co, seatId }: { api: Api; co: string; seatId: string }) {
  const q = useQuery(seatBoardWakesQuery(api, co, seatId));
  const rows = q.data?.data ?? [];
  if (!rows.length) return null;
  return (
    <Table data-slot="board-wakes">
      <TableCaption className="inline-flex items-center gap-1.5">
        Board wakes, last {q.data?.days ?? 7} days
        <InfoTip label="About board wakes">
          Boards this DE owns wake it with what changed: a person's comment or move at once, the
          rest batched per the board's window. Each wake is a short run on its light model; past the
          board's daily cap only urgent events wake it.
        </InfoTip>
      </TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">Board</TableHead>
          <TableHead scope="col" className="text-right">
            Wakes / day
          </TableHead>
          <TableHead scope="col" className="text-right">
            Events / wake
          </TableHead>
          <TableHead scope="col" className="text-right">
            Today
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.board_id} data-board={r.board_id}>
            <TableHead scope="row" className="max-w-48 truncate font-normal">
              {r.board_name}
            </TableHead>
            <TableCell className="text-right tabular-nums">{r.wakes_per_day}</TableCell>
            <TableCell className="text-right tabular-nums">{r.events_per_wake ?? "–"}</TableCell>
            <TableCell
              className="text-right tabular-nums"
              title={r.dropped_cap ? `Held at the cap on ${r.dropped_cap} day(s)` : undefined}
            >
              {r.wakes_today} / {r.daily_cap}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Ledger({ rows, which }: { rows: CostEvent[]; which: "Client" | "Agency" }) {
  if (!rows.length)
    return (
      <EmptyState
        variant="card"
        icon={ReceiptTextIcon}
        title={`No ${which.toLowerCase()} cost rows this month`}
      />
    );
  return (
    <Table>
      <TableCaption>{which} ledger, newest first.</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">When</TableHead>
          <TableHead scope="col">Kind</TableHead>
          <TableHead scope="col">Meter</TableHead>
          <TableHead scope="col" className="text-right">
            Amount
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((e) => (
          <TableRow key={e.id} data-visibility={e.visibility}>
            <TableCell className="text-muted-foreground">{formatDateTime(e.occurred_at)}</TableCell>
            <TableHead scope="row" className="font-normal">
              {COST_KIND_LABELS[e.kind] ?? e.kind}
            </TableHead>
            <TableCell className="max-w-48 truncate font-mono text-xs" title={e.meter_identity}>
              {e.meter_identity}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatMoney(e.amount_minor, e.currency)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
